import React, { useRef } from 'react';

import { correctingTrailingNumbers } from '@cryptoboyio/mercuryo-lab/utils';
import { nanoid } from '@reduxjs/toolkit';
import { useTranslation } from 'react-i18next';

import { bemCssModules } from '@utils';
import { useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { notificationsActions, useStore } from '@modules';
import { AccountsBuyResponse } from '@modules/accounts/types';
import { CurrencyEnum, Currencies } from '@modules/global/types';

import { RateType } from '@components/ConverterOld';
import CopyField from '@components/CopyField';
import Dropdown from '@components/Dropdown';
import Details from '@containers/Details';
import WalletLayout from '@layouts/WalletLayout';

import ShareIcon from './share_icon.svg';

import style from './AccountsPaymentDetails.scss';

enum DropdownEnum {
  copy = 'Copy account details',
  send = 'Send via email',
}

const menuItems = ['Copy account details', 'Send via email'];

const cn = bemCssModules(style, 'AccountsPaymentDetails');

export type TAccountsPaymentDetails = {
  paymentDetails?: AccountsBuyResponse;
  detailsData?: {
    rate?: RateType;
    amountSubtotal?: string;
    amountTotal: string;
    currency?: CurrencyEnum;
    fee?: string;
    feeCurrency?: CurrencyEnum;
    operationType: string;
    currencies: Currencies;
  };
};

const AccountsPaymentDetails: CustomFC<TAccountsPaymentDetails> = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ global }, dispatch] = useStore();
  const { currencies } = global;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation<LocationStateType['accountsBuyInvoiceDetails']>();
  const { state } = location;
  const { detailsData, paymentDetails } = state || {};

  const { iban, swift, full_name, bank_name, bank_address, address } =
    paymentDetails?.instructions || {};

  const { currency, rate, amountTotal } = detailsData;

  const hasRateDetails = rate && currency;

  const currenciesLib = currencies.config.display_options || null;

  const fields = [
    {
      caption: t('informer.amount'),
      value:
        amountTotal &&
        currency &&
        correctingTrailingNumbers(
          amountTotal,
          currenciesLib[currency]?.total_digits,
          currenciesLib[currency]?.display_digits
        ),
    },
    {
      caption: t('public.iban'),
      value: iban,
    },
    {
      caption: t('public.swift_code'),
      value: swift,
    },
    {
      caption: t('sepa.recipient'),
      value: full_name,
    },
    {
      caption: t('sepa.purpose_of_payment'),
      value: paymentDetails?.id || 'nigi14124VHJGV',
    },
    {
      caption: t('sepa.recipient_country'),
      value: address?.country,
    },
    {
      caption: t('public.address'),
      value: `${address?.city}, ${address?.address_line}`,
    },
    {
      caption: t('sepa.bank_name'),
      value: bank_name,
    },
    {
      caption: t('sepa.bank_address'),
      value: `${bank_address?.city}, ${bank_address?.address_line}`,
    },
  ];

  const fieldGroups = [fields.slice(0, 5), fields.slice(5, 9)];

  const formattingFields = (separator: string) =>
    fields.map(({ caption, value }) => `${caption}: ${value}`).join(separator);

  const onChange = (value: string) => {
    if (value === DropdownEnum.copy) {
      const copyText = formattingFields('\n');

      navigator.clipboard.writeText(copyText);
      dispatch(notificationsActions.add({ message: t('operations.copied') }));
    }

    if (value === DropdownEnum.send) {
      const body = formattingFields('%0D%0A');

      const element = document.createElement('a');
      element.href = `mailto:Enter e-mail?subject=Payment Details&body=${body}`;
      element.click();
      element.remove();
    }
  };

  return (
    <WalletLayout
      className={cn(null, [className])}
      header={
        <div className={cn('title')}>
          {t('iban.payment_details')}
          <button type="button" className={cn('copy-all')} ref={buttonRef}>
            <ShareIcon className={cn('icon')} />
          </button>
          <div className={cn('modal-wrapper')}>
            <Dropdown data={menuItems} controller={buttonRef} onChange={onChange} />
          </div>
        </div>
      }
      backButton={() => navigate<'wallets'>(CONST.nav.wallets, { state: { cacheConverter: true } })}
    >
      <div className={cn('text')}>
        <p>{t('iban.payment_details_p1')}</p>
        <p>{t('iban.payment_details_p2')}</p>
        <p>{t('iban.payment_details_p3')}</p>
      </div>

      {hasRateDetails && (
        <Details className={cn('details')} from={currency} operationRate={rate} withRateDetails />
      )}

      {fieldGroups.map(fieldGroup => (
        <div className={cn('input-group')}>
          {fieldGroup.map(({ caption, value = '' }) => (
            <CopyField key={nanoid()} caption={caption} value={value} />
          ))}
        </div>
      ))}
    </WalletLayout>
  );
};

export default AccountsPaymentDetails;
